import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, BACKEND_URL, photoSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TypeBadge, MinorBadge, RiskBadge } from "../components/Badges";
import { AdminBreakdownCharts } from "../components/AdminBreakdownCharts";
import { STATUS_OPTIONS } from "../lib/adminConstants";
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid,
} from "recharts";
import {
  LayoutDashboard, Files, Phone, Flag, Copy, Users as UsersIcon, ScrollText, Download, X,
  AlertTriangle, Clock, ShieldAlert, Search as SearchIcon, ChevronRight, Trash2, RotateCcw, MessageSquare,
} from "lucide-react";

const TABS = [
  { key: "dashboard", label: "Panel", icon: LayoutDashboard },
  { key: "cases", label: "Casos", icon: Files, countKey: "cases" },
  { key: "contacts", label: "Contacto", icon: Phone, countKey: "contacts" },
  { key: "messages", label: "Mensajes", icon: MessageSquare, countKey: "messages" },
  { key: "abuse", label: "Abuso", icon: Flag, countKey: "abuse" },
  { key: "duplicates", label: "Duplicados", icon: Copy, countKey: "duplicates" },
  { key: "users", label: "Usuarios", icon: UsersIcon, countKey: "users" },
  { key: "audit", label: "Auditoría", icon: ScrollText, countKey: "audit" },
  { key: "deleted", label: "Eliminados", icon: Trash2, countKey: "deleted" },
];

const EMPTY_FILTERS = { status: "", risk_level: "", is_minor: "", review_required: "", case_type: "", q: "" };

export default function Admin() {
  const [tab, setTab] = useState("dashboard");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [counts, setCounts] = useState({});

  const refreshCounts = useCallback(() => {
    api.get("/admin/tab-counts").then((r) => setCounts(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    refreshCounts();
    const id = setInterval(refreshCounts, 60000);
    return () => clearInterval(id);
  }, [refreshCounts]);

  const goCases = (f) => { setFilters({ ...EMPTY_FILTERS, ...f }); setTab("cases"); };
  const goTab = (t) => setTab(t);

  return (
    <div className="space-y-5">
      <h1 className="font-heading text-2xl font-bold text-[#0F172A]">Panel de administración</h1>
      <div className="flex gap-1 overflow-x-auto border-b border-[#E2E8F0] -mx-4 px-4">
        {TABS.map((t) => {
          const cnt = t.countKey ? counts[t.countKey] : undefined;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`flex items-center gap-1.5 px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${tab === t.key ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-[#475569]"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
              {cnt != null && <span data-testid={`tab-count-${t.key}`} className="text-xs font-semibold text-[#94A3B8]">({cnt})</span>}
            </button>
          );
        })}
      </div>
      {tab === "dashboard" && <Dashboard goCases={goCases} goTab={goTab} />}
      {tab === "cases" && <CasesTab filters={filters} setFilters={setFilters} refreshCounts={refreshCounts} />}
      {tab === "contacts" && <ContactsTab />}
      {tab === "messages" && <MessagesTab refreshCounts={refreshCounts} />}
      {tab === "abuse" && <AbuseTab />}
      {tab === "duplicates" && <DuplicatesTab refreshCounts={refreshCounts} />}
      {tab === "users" && <UsersTab />}
      {tab === "audit" && <AuditTab />}
      {tab === "deleted" && <DeletedTab refreshCounts={refreshCounts} />}
    </div>
  );
}

function Dashboard({ goCases, goTab }) {
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [act, setAct] = useState(null);
  useEffect(() => {
    api.get("/admin/metrics").then((r) => setM(r.data));
    api.get("/admin/activity").then((r) => setAct(r.data));
  }, []);
  if (!m) return <p className="text-[#475569]">Cargando…</p>;

  const cards = [
    ["Total reportes", m.total, () => goCases({})],
    ["Desaparecidas", m.missing, () => goCases({ case_type: "missing" })],
    ["Encontradas", m.found, () => goCases({ case_type: "found_alive" })],
    ["Reunificadas", m.reunified, () => goCases({ status: "reunified" })],
    ["Cerradas", m.closed, () => goCases({ status: "closed" })],
    ["Pend. revisión", m.pending_review, () => goCases({ review_required: "true" })],
    ["Menores", m.minors, () => goCases({ is_minor: "true" })],
    ["Riesgo alto/crítico", m.high_risk, () => goCases({ risk_level: "high" })],
    ["Solicitudes contacto", m.contact_pending, () => goTab("contacts")],
    ["Reportes abuso", m.abuse_pending, () => goTab("abuse")],
    ["Duplicados", m.duplicates, () => goTab("duplicates")],
    ["Usuarios", m.new_users, () => goTab("users")],
  ];

  const attention = [
    ["Casos pendientes de revisión", m.pending_review, () => goCases({ review_required: "true" }), Clock],
    ["Casos de menores", m.minors, () => goCases({ is_minor: "true" }), ShieldAlert],
    ["Riesgo alto/crítico", m.high_risk, () => goCases({ risk_level: "high" }), AlertTriangle],
    ["Solicitudes de contacto", m.contact_pending, () => goTab("contacts"), Phone],
    ["Reportes de abuso", m.abuse_pending, () => goTab("abuse"), Flag],
    ["Posibles duplicados", m.duplicates, () => goTab("duplicates"), Copy],
  ];

  const perDay = (m.per_day || []).map((d) => ({ name: d.date.slice(5), value: d.count }));

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Requiere atención */}
      <section>
        <h2 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[#D97706]" /> Requiere atención</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="admin-attention">
          {attention.map(([label, val, fn, Icon]) => (
            <button key={label} onClick={fn} data-testid={`attention-${label}`}
              className="text-left bg-white border border-[#E2E8F0] rounded-xl p-3 hover:border-[#1E3A8A] transition-colors">
              <div className="flex items-center justify-between">
                <Icon className="w-4 h-4 text-[#D97706]" />
                <span className={`font-heading text-xl font-bold ${val > 0 ? "text-[#0F172A]" : "text-[#CBD5E1]"}`}>{val ?? 0}</span>
              </div>
              <p className="text-xs text-[#475569] mt-1 leading-tight">{label}</p>
              <span className="text-xs text-[#1E3A8A] font-medium flex items-center gap-0.5 mt-1">Revisar <ChevronRight className="w-3 h-3" /></span>
            </button>
          ))}
        </div>
      </section>

      {/* Tarjetas clicables */}
      <section>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {cards.map(([label, val, fn]) => (
            <button key={label} onClick={fn} data-testid={`metric-card-${label}`}
              className="text-left bg-white border border-[#E2E8F0] rounded-xl p-4 hover:border-[#1E3A8A] transition-colors">
              <p className="text-xs uppercase tracking-wide font-semibold text-[#94A3B8]">{label}</p>
              <p className="font-heading text-2xl font-bold text-[#0F172A] mt-1">{val ?? 0}</p>
            </button>
          ))}
        </div>
      </section>

      {/* Acciones rápidas */}
      <section className="bg-white border border-[#E2E8F0] rounded-xl p-4">
        <h2 className="font-heading text-lg font-semibold mb-3">Acciones rápidas</h2>
        <div className="flex flex-wrap gap-2" data-testid="admin-quick-actions">
          <QA onClick={() => goCases({ review_required: "true" })} label="Revisar pendientes" />
          <QA onClick={() => goCases({ is_minor: "true" })} label="Revisar menores" />
          <QA onClick={() => goTab("abuse")} label="Revisar abuso" />
          <QA onClick={() => goTab("contacts")} label="Revisar solicitudes" />
          <QA onClick={() => goTab("duplicates")} label="Ver duplicados" />
          <QA onClick={downloadCsv} label="Exportar CSV" icon={Download} />
          <QA onClick={() => navigate("/buscar")} label="Búsqueda pública" icon={SearchIcon} />
        </div>
      </section>

      {/* Gráfica de reportes por día */}
      <section className="grid grid-cols-1 gap-4">
        <ChartCard title="Reportes por día (últimos 14)" full>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={perDay} margin={{ left: -20, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#1E3A8A" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      {/* Estadísticas por zona/género/menores */}
      <AdminBreakdownCharts />

      {/* Actividad reciente */}
      <section className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="admin-activity">
        <h2 className="font-heading text-lg font-semibold mb-3">Actividad reciente</h2>
        {!act ? <p className="text-sm text-[#94A3B8]">Cargando…</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
            {act.reports?.map((r) => (
              <ActItem key={r.id} text={`Nuevo reporte ${r.public_case_code} (${r.case_type === "missing" ? "desaparecida" : "encontrada"})`}
                date={r.created_at} onView={() => navigate(`/caso/${r.public_case_code}`)} />
            ))}
            {act.contacts?.map((r) => (
              <ActItem key={r.id} text={`Solicitud ${r.request_type === "think_relative" ? "familiar" : "de info"} · ${r.case_code}`}
                date={r.created_at} onView={() => goTab("contacts")} />
            ))}
            {act.abuse?.map((r) => (
              <ActItem key={r.id} text={`Reporte de abuso (${r.reason}) · ${r.case_code}`} date={r.created_at} onView={() => goTab("abuse")} />
            ))}
            {act.admin_actions?.slice(0, 4).map((r) => (
              <ActItem key={r.id} text={`Acción: ${r.action} · ${r.entity_type}`} date={r.created_at} onView={() => goTab("audit")} />
            ))}
          </div>
        )}
      </section>

      <p className="text-xs text-[#94A3B8] leading-relaxed bg-[#F1F5F9] rounded-lg p-3" data-testid="admin-note">
        Los casos de bajo riesgo se publican de forma limitada automáticamente. Este panel prioriza casos sensibles, abuso, menores, duplicados y solicitudes que requieren revisión.
      </p>
    </div>
  );
}

function QA({ onClick, label, icon: Icon }) {
  return (
    <button onClick={onClick} data-testid={`quick-${label}`}
      className="flex items-center gap-1.5 h-10 px-3 rounded-lg bg-[#F1F5F9] text-sm font-medium text-[#0F172A] hover:bg-[#E2E8F0]">
      {Icon && <Icon className="w-4 h-4" />} {label}
    </button>
  );
}
function ChartCard({ title, children, full }) {
  return (
    <div className={`bg-white border border-[#E2E8F0] rounded-xl p-4 ${full ? "lg:col-span-2" : ""}`}>
      <h3 className="font-heading font-semibold mb-3 text-sm">{title}</h3>
      {children}
    </div>
  );
}
function Empty() { return <p className="text-sm text-[#94A3B8] py-8 text-center">Sin datos aún.</p>; }

function ActItem({ text, date, onView }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b border-[#F1F5F9] text-sm">
      <span className="text-[#0F172A] truncate">{text}</span>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs text-[#94A3B8]">{date ? new Date(date).toLocaleDateString() : ""}</span>
        <button onClick={onView} className="text-xs text-[#1E3A8A] font-medium underline">Ver</button>
      </div>
    </div>
  );
}

function CasesTab({ filters, setFilters, refreshCounts }) {
  const [data, setData] = useState({ results: [], total: 0 });
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState(null);
  const PAGE = 20;

  const buildParams = useCallback((pg) => {
    const p = { page: pg, limit: PAGE };
    Object.entries(filters).forEach(([k, v]) => { if (v !== "") p[k] = v; });
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    const r = await api.get("/admin/cases", { params: buildParams(1) });
    setData(r.data); setPage(1);
  }, [buildParams]);
  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = page + 1;
      const r = await api.get("/admin/cases", { params: buildParams(next) });
      setData((prev) => ({ total: r.data.total, results: [...prev.results, ...r.data.results] }));
      setPage(next);
    } finally { setLoadingMore(false); }
  };

  const onMutate = () => { load(); refreshCounts?.(); };

  return (
    <div className="space-y-4" data-testid="admin-cases">
      <div className="flex flex-wrap gap-2">
        <Select value={filters.case_type} onChange={(v) => setFilters({ ...filters, case_type: v })} testid="admin-filter-type">
          <option value="">Tipo (todos)</option>
          <option value="missing">Desaparecida</option><option value="found_alive">Encontrada</option>
        </Select>
        <Select value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} testid="admin-filter-status">
          <option value="">Estado (todos)</option>
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>
        <Select value={filters.risk_level} onChange={(v) => setFilters({ ...filters, risk_level: v })} testid="admin-filter-risk">
          <option value="">Riesgo (todos)</option>
          <option value="low">Bajo</option><option value="medium">Medio</option>
          <option value="high">Alto</option><option value="critical">Crítico</option>
        </Select>
        <Select value={filters.is_minor} onChange={(v) => setFilters({ ...filters, is_minor: v })} testid="admin-filter-minor">
          <option value="">Menores (todos)</option><option value="true">Solo menores</option>
        </Select>
        <Select value={filters.review_required} onChange={(v) => setFilters({ ...filters, review_required: v })} testid="admin-filter-review">
          <option value="">Revisión (todos)</option><option value="true">Requiere revisión</option>
        </Select>
        <button onClick={() => setFilters(EMPTY_FILTERS)} data-testid="admin-filter-clear"
          className="h-10 px-3 rounded-lg bg-[#F1F5F9] text-sm font-medium text-[#475569]">Limpiar</button>
        <button onClick={downloadCsv} data-testid="admin-export-btn"
          className="flex items-center gap-1.5 h-10 px-3 rounded-lg bg-[#F1F5F9] text-sm font-medium text-[#0F172A]">
          <Download className="w-4 h-4" /> Exportar CSV
        </button>
      </div>
      <p className="text-sm text-[#475569]" data-testid="admin-cases-count">Mostrando {data.results.length} de {data.total} casos</p>
      <div className="space-y-2">
        {data.results.map((c) => (
          <button key={c.id} onClick={() => setSelected(c.id)} data-testid={`admin-case-${c.public_case_code}`}
            className="w-full text-left bg-white border border-[#E2E8F0] rounded-xl p-3 flex items-center gap-3 hover:border-[#1E3A8A]">
            <div className="w-12 h-12 rounded-lg bg-[#F1F5F9] overflow-hidden shrink-0 relative flex items-center justify-center">
              <Files className="w-5 h-5 text-[#CBD5E1]" />
              {c.photo_url && (
                <img src={photoSrc(c.photo_url)} alt="" className="absolute inset-0 w-full h-full object-cover"
                  onLoad={(e) => { if (e.currentTarget.naturalWidth <= 1) e.currentTarget.style.display = "none"; }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <TypeBadge caseType={c.case_type} label={c.case_type_label} />
                <RiskBadge level={c.risk_level} />
                {c.is_minor_case && <MinorBadge />}
                <span data-testid={`case-status-${c.public_case_code}`}
                  className="text-xs font-medium px-2 py-0.5 rounded bg-[#EFF6FF] text-[#1E40AF]">
                  Estado: {c.status_label || c.status}
                </span>
                {!c.public_visibility && <span className="text-xs font-medium px-2 py-0.5 rounded bg-[#F1F5F9] text-[#64748B]">Oculto</span>}
                {c.review_required && <span className="text-xs text-[#9A3412] font-medium">• revisión</span>}
              </div>
              <p className="font-medium text-sm mt-1 truncate">{c.first_name || "Sin nombre"} · <span className="font-mono text-[#94A3B8]">{c.public_case_code}</span></p>
            </div>
          </button>
        ))}
        {data.results.length === 0 && <p className="text-sm text-[#94A3B8] py-8 text-center">No hay casos con estos filtros.</p>}
      </div>
      {data.results.length < data.total && (
        <div className="flex justify-center pt-1">
          <button onClick={loadMore} disabled={loadingMore} data-testid="admin-cases-load-more"
            className="h-11 px-8 rounded-xl bg-white border border-[#1E3A8A] text-[#1E3A8A] font-semibold disabled:opacity-50">
            {loadingMore ? "Cargando…" : `Cargar más (${data.total - data.results.length} restantes)`}
          </button>
        </div>
      )}
      {selected && <CaseDrawer caseId={selected} onClose={() => { setSelected(null); onMutate(); }} onMutate={onMutate} />}
    </div>
  );
}

async function downloadCsv() {
  const tid = toast.loading("Generando CSV…");
  try {
    const res = await api.get("/admin/export/cases.csv", { responseType: "blob" });
    const date = new Date().toISOString().slice(0, 10);
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `reencuentro-casos-${date}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("CSV descargado.", { id: tid });
  } catch (e) {
    if (e.response?.status === 401 || e.response?.status === 403) {
      toast.error("No autorizado para exportar.", { id: tid });
    } else {
      toast.error("No se pudo generar el CSV.", { id: tid });
    }
  }
}

function CaseDrawer({ caseId, onClose, onMutate }) {
  const [c, setC] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [delReason, setDelReason] = useState("");
  useEffect(() => {
    api.get(`/admin/cases/${caseId}`).then((r) => { setC(r.data); setNotes(r.data.internal_notes || ""); });
  }, [caseId]);

  const patch = async (body, msg) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/cases/${caseId}`, body);
      setC(data);
      toast.success(msg || "Caso actualizado.");
      onMutate?.();
    } catch (e) {
      toast.error("No se pudo actualizar el caso. Intenta de nuevo.");
    } finally { setBusy(false); }
  };

  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/cases/${caseId}`, { data: { reason: delReason || null } });
      toast.success("Caso eliminado. Ya no aparece en búsqueda ni listas.");
      setShowDelete(false);
      onClose();
    } catch (e) {
      toast.error("No se pudo eliminar el caso.");
    } finally { setBusy(false); }
  };

  if (!c) return null;
  const rep = c.reporter_contact || {};
  const loc = c.last_known_location || c.found_location || {};

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-case-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">{c.public_case_code}</h2>
          <button onClick={onClose} data-testid="admin-drawer-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <TypeBadge caseType={c.case_type} label={c.case_type_label} />
          <RiskBadge level={c.risk_level} />
          {c.is_minor_case && <MinorBadge />}
        </div>

        <Section title="Persona">
          <KV k="Nombre" v={[c.person?.first_name, c.person?.last_name].filter(Boolean).join(" ")} />
          <KV k="Edad" v={c.person?.approximate_age} />
          <KV k="C.I. número (privada)" v={c.person?.venezuelan_id_number} />
          <KV k="Datos médicos (privados)" v={c.person?.medical_notes_private} />
        </Section>

        <Section title="Ubicación (privada)">
          <KV k="Zona" v={[loc.municipality, loc.state_region].filter(Boolean).join(", ")} />
          <KV k="Dirección exacta" v={loc.exact_address_private} />
        </Section>

        <Section title="Reportante (privado)">
          <KV k="Nombre" v={[rep.first_name, rep.last_name].filter(Boolean).join(" ")} />
          <KV k="Teléfono" v={rep.phone} />
          <KV k="Email" v={rep.email} />
          <KV k="Motivo" v={rep.report_reason} />
          <KV k="Relación" v={rep.relationship_to_person} />
        </Section>

        <Section title="Acciones">
          <label className="text-xs font-semibold text-[#475569]">Cambiar estado</label>
          <select value={c.status} disabled={busy} onChange={(e) => patch({ status: e.target.value }, "Estado actualizado.")} data-testid="admin-status-select"
            className="w-full h-11 px-3 rounded-lg border border-[#E2E8F0] bg-white mb-2 disabled:opacity-50">
            {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <label className="text-xs font-semibold text-[#475569]">Nivel de riesgo</label>
          <select value={c.risk_level} disabled={busy} onChange={(e) => patch({ risk_level: e.target.value }, "Nivel de riesgo actualizado.")} data-testid="admin-risk-select"
            className="w-full h-11 px-3 rounded-lg border border-[#E2E8F0] bg-white mb-2 disabled:opacity-50">
            <option value="low">Bajo</option><option value="medium">Medio</option>
            <option value="high">Alto</option><option value="critical">Crítico</option>
          </select>
          <div className="flex gap-2 mb-2">
            <button onClick={() => patch({ public_visibility: !c.public_visibility }, c.public_visibility ? "Ficha oculta." : "Ficha publicada.")} disabled={busy} data-testid="admin-toggle-visibility"
              className="flex-1 h-11 rounded-lg bg-[#F1F5F9] text-sm font-medium disabled:opacity-50">
              {c.public_visibility ? "Ocultar ficha" : "Publicar ficha"}
            </button>
            <button onClick={() => patch({ status: "reunified" }, "Marcado como reunificado.")} disabled={busy} data-testid="admin-mark-reunified"
              className="flex-1 h-11 rounded-lg bg-[#D1FAE5] text-[#065F46] text-sm font-medium disabled:opacity-50">Reunificado</button>
          </div>
          <button onClick={() => patch({ status: "closed", closure_reason: "admin_close" }, "Caso cerrado.")} disabled={busy} data-testid="admin-close-case"
            className="w-full h-11 rounded-lg bg-[#F1F5F9] text-sm font-medium mb-2 disabled:opacity-50">Cerrar caso</button>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notas internas" rows={2} data-testid="admin-notes"
            className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] mb-2 text-sm" />
          <button onClick={() => patch({ internal_notes: notes }, "Notas guardadas.")} disabled={busy} data-testid="admin-save-notes"
            className="w-full h-11 rounded-lg bg-[#1E3A8A] text-white text-sm font-semibold mb-2 disabled:opacity-50">Guardar notas</button>
          <button onClick={() => setShowDelete(true)} disabled={busy} data-testid="admin-delete-case"
            className="w-full h-11 rounded-lg bg-[#FEE2E2] text-[#991B1B] text-sm font-semibold disabled:opacity-50">Eliminar caso</button>
        </Section>
      </div>

      {showDelete && (
        <div className="absolute inset-0 z-10 flex items-center justify-center p-4" data-testid="admin-delete-dialog">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDelete(false)} />
          <div className="relative bg-white rounded-2xl p-5 max-w-sm w-full space-y-3">
            <h3 className="font-heading text-lg font-bold text-[#0F172A]">¿Eliminar caso?</h3>
            <p className="text-sm text-[#475569]">Este caso dejará de aparecer en la búsqueda pública y en las listas principales. La información se conservará de forma restringida para auditoría.</p>
            <label className="block">
              <span className="text-xs font-semibold text-[#475569]">Motivo de eliminación (opcional)</span>
              <textarea value={delReason} onChange={(e) => setDelReason(e.target.value)} rows={2} data-testid="admin-delete-reason"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
            </label>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setShowDelete(false)} disabled={busy} data-testid="admin-delete-cancel"
                className="flex-1 h-12 rounded-xl bg-[#F1F5F9] text-[#0F172A] font-semibold disabled:opacity-50">Cancelar</button>
              <button onClick={del} disabled={busy} data-testid="admin-delete-confirm"
                className="flex-1 h-12 rounded-xl bg-[#991B1B] text-white font-semibold disabled:opacity-50">
                {busy ? "Eliminando…" : "Eliminar caso"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const CONTACT_STATUS_LABELS = {
  new: "Nueva", notified_reporter: "Reportante notificado", viewed_by_reporter: "Vista por reportante",
  reporter_replied: "Reportante respondió", more_info_requested: "Solicitó más info",
  contact_shared_by_responder: "Contacto compartido (solicitante)", contact_shared_by_reporter: "Contacto compartido (reportante)",
  contact_shared_mutual: "Contacto compartido (mutuo)", discarded: "Descartada",
  abuse_reported: "Abuso reportado", closed: "Cerrada", blocked: "Bloqueada",
};

function ContactsTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/contact-requests").then((r) => setItems(r.data.results));
  useEffect(() => { load(); }, []);
  const review = async (id, status) => { await api.patch(`/admin/contact-requests/${id}`, { status }); toast.success("Actualizado."); load(); };
  return (
    <div className="space-y-3" data-testid="admin-contacts">
      <p className="text-sm text-[#475569] bg-[#F1F5F9] rounded-lg p-3">
        Supervisión operativa de solicitudes (no aprobación). El contacto entre las partes es autónomo. Interviene solo por abuso, soporte o seguridad.
      </p>
      {items.length === 0 && <p className="text-[#475569]">Sin solicitudes.</p>}
      {items.map((r) => (
        <div key={r.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-1" data-testid={`admin-contact-${r.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold px-2 py-0.5 rounded bg-[#EFF6FF] text-[#1E40AF]">
              {r.request_type === "think_relative" ? "Posible familiar" : "Tiene información"}
            </span>
            <span className="text-xs font-mono text-[#94A3B8]">{r.case_code}</span>
            <span className="text-xs font-semibold px-2 py-0.5 rounded bg-[#F1F5F9] text-[#475569]">{CONTACT_STATUS_LABELS[r.status] || r.status}</span>
            {r.last_email_status && <span className="text-xs text-[#94A3B8]">email: {r.last_email_status}</span>}
          </div>
          <p className="text-sm font-medium">{r.requester_first_name} {r.requester_last_name}</p>
          <div className="flex flex-wrap gap-2 text-xs text-[#64748B]">
            <span>Notificado: {r.reporter_notified ? "sí" : "no"}</span>
            <span>· Consent. solicitante: {r.responder_share_consent ? "sí" : "no"}</span>
            <span>· Consent. reportante: {r.reporter_share_consent ? "sí" : "no"}</span>
          </div>
          {r.relationship_claim && <p className="text-xs text-[#475569]">Relación: {r.relationship_claim}</p>}
          <p className="text-sm text-[#0F172A]">{r.message}</p>
          <div className="flex gap-2 pt-2">
            <button onClick={() => review(r.id, "closed")} data-testid={`contact-close-${r.id}`} className="h-9 px-3 rounded-lg bg-[#F1F5F9] text-[#0F172A] text-sm font-medium">Cerrar</button>
            <button onClick={() => review(r.id, "blocked")} data-testid={`contact-block-${r.id}`} className="h-9 px-3 rounded-lg bg-[#FEE2E2] text-[#991B1B] text-sm font-medium">Bloquear por abuso</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AbuseTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/abuse-reports").then((r) => setItems(r.data.results));
  useEffect(() => { load(); }, []);
  const review = async (id, status) => { await api.patch(`/admin/abuse-reports/${id}`, { status }); load(); };
  return (
    <div className="space-y-3" data-testid="admin-abuse">
      {items.length === 0 && <p className="text-[#475569]">Sin reportes de abuso.</p>}
      {items.map((r) => (
        <div key={r.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-1" data-testid={`admin-abuse-${r.id}`}>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold px-2 py-0.5 rounded bg-[#FEF2F2] text-[#991B1B]">{r.reason}</span>
            <span className="text-xs font-mono text-[#94A3B8]">{r.case_code}</span>
            <span className="text-xs text-[#94A3B8]">· {r.status}</span>
          </div>
          {r.description && <p className="text-sm text-[#0F172A]">{r.description}</p>}
          <div className="flex gap-2 pt-2">
            <button onClick={() => review(r.id, "resolved")} data-testid={`abuse-resolve-${r.id}`} className="h-9 px-3 rounded-lg bg-[#D1FAE5] text-[#065F46] text-sm font-medium">Resolver</button>
            <button onClick={() => review(r.id, "dismissed")} data-testid={`abuse-dismiss-${r.id}`} className="h-9 px-3 rounded-lg bg-[#F1F5F9] text-sm font-medium">Descartar</button>
          </div>
        </div>
      ))}
    </div>
  );
}

const MSG_TYPE_LABELS = {
  technical: "Falla técnica", privacy: "Problema de privacidad",
  correction: "Solicitud de corrección", general: "Duda general", other: "Otro",
};
const MSG_STATUS = [
  { value: "new", label: "Nuevo" }, { value: "in_review", label: "En revisión" },
  { value: "resolved", label: "Resuelto" }, { value: "closed", label: "Cerrado" },
];
const MSG_STATUS_STYLE = {
  new: "bg-[#FEF3C7] text-[#92400E]", in_review: "bg-[#DBEAFE] text-[#1E40AF]",
  resolved: "bg-[#D1FAE5] text-[#065F46]", closed: "bg-[#F1F5F9] text-[#64748B]",
};

function MessagesTab({ refreshCounts }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [notes, setNotes] = useState({});
  const load = useCallback(() => {
    const params = filter ? { status: filter } : {};
    api.get("/admin/messages", { params }).then((r) => setItems(r.data.results));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (m, status) => {
    await api.patch(`/admin/messages/${m.id}/status`, { status, internal_notes: notes[m.id] ?? m.internal_notes ?? null });
    toast.success("Estado actualizado.");
    load(); refreshCounts?.();
  };
  const saveNotes = async (m) => {
    await api.patch(`/admin/messages/${m.id}/status`, { status: m.status, internal_notes: notes[m.id] ?? "" });
    toast.success("Notas guardadas.");
    load();
  };

  return (
    <div className="space-y-3" data-testid="admin-messages">
      <div className="flex items-center gap-2">
        <Select value={filter} onChange={setFilter} testid="messages-filter-status">
          <option value="">Estado (todos)</option>
          {MSG_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>
        <span className="text-sm text-[#475569]" data-testid="messages-count">{items.length} mensaje(s)</span>
      </div>
      {items.length === 0 && <p className="text-sm text-[#94A3B8] py-8 text-center">No hay mensajes con este filtro.</p>}
      {items.map((m) => (
        <div key={m.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-2" data-testid={`admin-message-${m.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold px-2 py-0.5 rounded bg-[#EFF6FF] text-[#1E40AF]">{m.message_type_label || MSG_TYPE_LABELS[m.message_type]}</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${MSG_STATUS_STYLE[m.status] || ""}`} data-testid={`message-status-${m.id}`}>
              {MSG_STATUS.find((s) => s.value === m.status)?.label || m.status}
            </span>
            {m.related_case_code && <span className="text-xs font-mono text-[#94A3B8]">{m.related_case_code}</span>}
            <span className="text-xs text-[#94A3B8] ml-auto">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</span>
          </div>
          <p className="text-sm text-[#0F172A] whitespace-pre-wrap break-words">{m.message}</p>
          <div className="text-xs text-[#64748B]">
            <span className="font-medium">{m.name || "Anónimo"}</span>
            {m.contact && <span> · contacto privado: {m.contact}</span>}
          </div>
          <textarea value={notes[m.id] ?? m.internal_notes ?? ""} onChange={(e) => setNotes({ ...notes, [m.id]: e.target.value })}
            placeholder="Notas internas" rows={2} data-testid={`message-notes-${m.id}`}
            className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => saveNotes(m)} data-testid={`message-save-notes-${m.id}`}
              className="h-9 px-3 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium">Guardar notas</button>
            <select value={m.status} onChange={(e) => changeStatus(m, e.target.value)} data-testid={`message-status-select-${m.id}`}
              className="h-9 px-2 rounded-lg border border-[#E2E8F0] text-sm">
              {MSG_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>
      ))}
      <p className="text-xs text-[#94A3B8] leading-relaxed bg-[#F1F5F9] rounded-lg p-3">
        Los mensajes de Contáctanos son privados: solo visibles para Admin/Super Admin, no se publican, no se indexan ni se comparten en previews.
      </p>
    </div>
  );
}

const DUP_LEVEL = {
  strong: { label: "Coincidencia fuerte", cls: "bg-[#FEE2E2] text-[#991B1B]" },
  medium: { label: "Coincidencia media", cls: "bg-[#FEF3C7] text-[#92400E]" },
  low: { label: "Coincidencia baja", cls: "bg-[#EFF6FF] text-[#1E40AF]" },
};

function DupCaseBlock({ c, label }) {
  if (!c || c.missing || !c.public_case_code) {
    return (
      <div className="flex-1 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3">
        <p className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wide">{label}</p>
        <p className="text-sm text-[#94A3B8] mt-2">Código no disponible</p>
      </div>
    );
  }
  const name = c.first_name || c.nickname || "Sin nombre";
  return (
    <div className="flex-1 rounded-xl border border-[#E2E8F0] bg-white p-3 space-y-2" data-testid={`dup-block-${c.public_case_code}`}>
      <p className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wide">{label}</p>
      <div className="flex gap-3">
        <div className="w-14 h-14 rounded-lg bg-[#F1F5F9] overflow-hidden shrink-0 flex items-center justify-center">
          {c.photo_url ? (
            <img src={photoSrc(c.photo_url)} alt="" className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : <Files className="w-5 h-5 text-[#CBD5E1]" />}
        </div>
        <div className="min-w-0">
          <p className="font-mono text-sm font-semibold text-[#0F172A]">{c.public_case_code}</p>
          <p className="text-sm text-[#0F172A] truncate">{name}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <TypeBadge caseType={c.case_type} label={c.case_type_label} />
        <RiskBadge level={c.risk_level} />
        {c.is_minor_case && <MinorBadge />}
      </div>
      <div className="text-xs text-[#64748B] space-y-0.5">
        <p>Edad aprox.: {c.approximate_age ?? "—"} · Género: {c.sex || "—"}</p>
        <p>Zona: {c.approximate_zone || "—"}</p>
        <p>Estado: {c.status_label || c.status || "—"}</p>
        <p>Creado: {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</p>
      </div>
    </div>
  );
}

function DuplicatesTab({ refreshCounts }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [review, setReview] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/duplicates", { params: { status: filter } }).then((r) => setItems(r.data.results));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const after = () => { load(); refreshCounts?.(); };

  return (
    <div className="space-y-3" data-testid="admin-duplicates">
      <p className="text-sm text-[#475569] bg-[#F1F5F9] rounded-lg p-3">
        Revisa posibles reportes duplicados antes de fusionarlos o descartarlos. La información privada permanece protegida.
      </p>
      <div className="flex items-center gap-2">
        <Select value={filter} onChange={setFilter} testid="dup-filter-status">
          <option value="pending">Pendientes de revisión</option>
          <option value="keep_pending">Mantenidos como posible duplicado</option>
          <option value="not_duplicate">No es duplicado</option>
          <option value="merged">Fusionados</option>
        </Select>
        <span className="text-sm text-[#475569]" data-testid="dup-count">{items.length}</span>
      </div>

      {items.length === 0 && (
        <p className="text-sm text-[#94A3B8] py-8 text-center" data-testid="dup-empty">
          {filter === "pending" ? "No hay posibles duplicados pendientes." : "Sin registros con este filtro."}
        </p>
      )}

      {items.map((d) => {
        const lvl = DUP_LEVEL[d.match_level];
        return (
          <div key={d.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-3" data-testid={`admin-duplicate-${d.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="font-heading font-semibold text-[#0F172A]">Posible duplicado detectado</h3>
              {lvl && <span className={`text-xs font-semibold px-2 py-0.5 rounded ${lvl.cls}`}>{lvl.label}</span>}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <DupCaseBlock c={d.new_case} label="Caso nuevo" />
              <DupCaseBlock c={d.existing_case} label="Caso posiblemente duplicado" />
            </div>
            {d.match_reason && (
              <div className="text-sm">
                <span className="font-semibold text-[#0F172A]">Razón de coincidencia: </span>
                <span className="text-[#475569]">{d.match_reason}</span>
              </div>
            )}
            <button onClick={() => setReview(d)} data-testid={`dup-review-${d.id}`}
              className="h-10 px-4 rounded-lg bg-[#1E3A8A] text-white text-sm font-semibold">Revisar coincidencia</button>
          </div>
        );
      })}

      {review && <DupReviewDrawer dup={review} onClose={() => setReview(null)} onDone={() => { setReview(null); after(); }} />}
    </div>
  );
}

function DupReviewDrawer({ dup, onClose, onDone }) {
  const { user: me } = useAuth();
  const canMerge = ["admin", "super_admin"].includes(me?.role);
  const [busy, setBusy] = useState(false);
  const [confirmMerge, setConfirmMerge] = useState(false);
  const [primary, setPrimary] = useState(dup.new_case?.case_id || "");
  const [reason, setReason] = useState("");
  const lvl = DUP_LEVEL[dup.match_level];

  const act = async (path, body, msg) => {
    setBusy(true);
    try {
      await api.post(`/admin/duplicates/${dup.id}/${path}`, body || {});
      toast.success(msg);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo completar la acción.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="dup-review-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Revisar coincidencia</h2>
          <button onClick={onClose} data-testid="dup-review-close"><X className="w-5 h-5" /></button>
        </div>
        {lvl && <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded ${lvl.cls}`}>{lvl.label}</span>}
        <div className="flex flex-col sm:flex-row gap-3">
          <DupCaseBlock c={dup.new_case} label="Caso nuevo" />
          <DupCaseBlock c={dup.existing_case} label="Caso posiblemente duplicado" />
        </div>
        {dup.match_reason && (
          <div className="text-sm border-t border-[#E2E8F0] pt-3">
            <p className="font-semibold text-[#0F172A] mb-1">Razón de coincidencia</p>
            <p className="text-[#475569]">{dup.match_reason}</p>
          </div>
        )}

        <div className="border-t border-[#E2E8F0] pt-3 space-y-2">
          <p className="font-semibold text-sm text-[#0F172A]">Acciones</p>
          <button onClick={() => act("not-duplicate", null, "Marcado como: no es duplicado.")} disabled={busy} data-testid="dup-not-duplicate"
            className="w-full h-11 rounded-lg bg-[#F1F5F9] text-[#0F172A] text-sm font-medium disabled:opacity-50">No es duplicado</button>
          <button onClick={() => act("keep-pending", null, "Mantenido como posible duplicado.")} disabled={busy} data-testid="dup-keep-pending"
            className="w-full h-11 rounded-lg bg-[#FEF3C7] text-[#92400E] text-sm font-medium disabled:opacity-50">Mantener como posible duplicado</button>

          {canMerge && !confirmMerge && (
            <button onClick={() => setConfirmMerge(true)} disabled={busy} data-testid="dup-merge-open"
              className="w-full h-11 rounded-lg bg-[#1E3A8A] text-white text-sm font-semibold disabled:opacity-50">Fusionar casos</button>
          )}

          {canMerge && confirmMerge && (
            <div className="rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] p-3 space-y-3" data-testid="dup-merge-confirm">
              <p className="text-xs font-semibold text-[#475569]">Caso principal a conservar</p>
              <div className="space-y-1.5">
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="primary" checked={primary === dup.new_case?.case_id}
                    onChange={() => setPrimary(dup.new_case?.case_id)} disabled={!dup.new_case?.case_id} data-testid="dup-primary-new" />
                  Mantener caso nuevo ({dup.new_case?.public_case_code || "—"})
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="primary" checked={primary === dup.existing_case?.case_id}
                    onChange={() => setPrimary(dup.existing_case?.case_id)} disabled={!dup.existing_case?.case_id} data-testid="dup-primary-existing" />
                  Mantener caso existente ({dup.existing_case?.public_case_code || "—"})
                </label>
              </div>
              <h3 className="font-heading text-base font-bold text-[#991B1B]">¿Fusionar estos casos?</h3>
              <p className="text-xs text-[#475569] leading-relaxed">
                Esta acción consolidará la información de ambos reportes. El caso secundario dejará de mostrarse como caso independiente y quedará registrado como fusionado. Esta acción debe usarse solo si confirmaste que ambos reportes corresponden a la misma persona.
              </p>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (opcional)" data-testid="dup-merge-reason"
                className="w-full h-10 px-3 rounded-lg border border-[#E2E8F0] text-sm" />
              <div className="flex gap-2">
                <button onClick={() => setConfirmMerge(false)} disabled={busy} data-testid="dup-merge-cancel"
                  className="flex-1 h-11 rounded-lg bg-[#F1F5F9] text-[#0F172A] text-sm font-semibold disabled:opacity-50">Cancelar</button>
                <button onClick={() => act("merge", { primary_case_id: primary, reason: reason || null }, "Casos fusionados.")}
                  disabled={busy || !primary} data-testid="dup-merge-confirm-btn"
                  className="flex-1 h-11 rounded-lg bg-[#991B1B] text-white text-sm font-semibold disabled:opacity-50">
                  {busy ? "Fusionando…" : "Fusionar casos"}
                </button>
              </div>
            </div>
          )}
          {!canMerge && (
            <p className="text-xs text-[#94A3B8]">La fusión de casos solo está disponible para Admin/Super Admin.</p>
          )}
        </div>
        <p className="text-xs text-[#94A3B8] leading-relaxed">La fusión no elimina información: el caso secundario se conserva internamente, se oculta de la búsqueda pública y se mantienen historial, solicitudes de contacto y registros de auditoría.</p>
      </div>
    </div>
  );
}

function UsersTab() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setItems(r.data.results));
  useEffect(() => { load(); }, []);
  const update = async (id, body) => { await api.patch(`/admin/users/${id}`, body); load(); };
  return (
    <div className="space-y-2" data-testid="admin-users">
      {items.map((u) => (
        <div key={u.id} className="bg-white border border-[#E2E8F0] rounded-xl p-3 flex items-center gap-3 flex-wrap" data-testid={`admin-user-${u.id}`}>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">{u.first_name} {u.last_name}</p>
            <p className="text-xs text-[#94A3B8] truncate">{u.email}</p>
          </div>
          <select value={u.role} onChange={(e) => update(u.id, { role: e.target.value })} data-testid={`user-role-${u.id}`}
            disabled={me.role !== "super_admin" && ["admin", "super_admin"].includes(u.role)}
            className="h-9 px-2 rounded-lg border border-[#E2E8F0] text-sm">
            <option value="user">Usuario</option>
            <option value="verified_reporter">Reportante verificado</option>
            <option value="volunteer">Voluntario</option>
            {me.role === "super_admin" && <option value="admin">Admin</option>}
          </select>
          <button onClick={() => update(u.id, { status: u.status === "blocked" ? "active" : "blocked" })} data-testid={`user-block-${u.id}`}
            className={`h-9 px-3 rounded-lg text-sm font-medium ${u.status === "blocked" ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>
            {u.status === "blocked" ? "Desbloquear" : "Bloquear"}
          </button>
        </div>
      ))}
    </div>
  );
}

const AUDIT_ACTIONS = [
  ["create", "Creación"], ["admin_update", "Actualización (admin)"], ["edit", "Edición (propietario)"],
  ["soft_delete", "Eliminado (soft)"], ["restore", "Restaurado"], ["permanent_delete", "Eliminado definitivamente"],
  ["merge", "Fusión de duplicado"], ["review", "Revisión"], ["update", "Actualización"], ["export_csv", "Exportación CSV"],
  ["platform_message/create", "Mensaje de contacto"], ["platform_message/update_status", "Mensaje: cambio de estado"],
  ["duplicate/not_duplicate", "Duplicado: no es duplicado"], ["duplicate/keep_pending", "Duplicado: mantener pendiente"], ["duplicate/merge", "Duplicado: fusión de casos"],
];
const AUDIT_ROLES = [["admin", "Admin"], ["super_admin", "Super Admin"], ["volunteer", "Voluntario"], ["verified_reporter", "Reportante verificado"], ["user", "Usuario"], ["public", "Público"]];
const AUDIT_ENTITIES = [["case", "Caso"], ["contact_request", "Solicitud de contacto"], ["abuse_report", "Reporte de abuso"], ["platform_message", "Mensaje de contacto"], ["case_duplicate", "Duplicado"], ["user", "Usuario"], ["export", "Exportación"]];
const EMPTY_AUDIT_FILTERS = { action: "", actor_role: "", entity_type: "", actor: "", case_code: "", date_from: "", date_to: "" };

function AuditTab() {
  const [filters, setFilters] = useState(EMPTY_AUDIT_FILTERS);
  const [data, setData] = useState({ results: [], total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [detail, setDetail] = useState(null);
  const PAGE = 25;

  const buildParams = (pg, f) => {
    const p = { page: pg, limit: PAGE };
    Object.entries(f).forEach(([k, v]) => { if (v !== "") p[k] = v; });
    return p;
  };

  const fetchPage = async (pg, append, f = filters) => {
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const r = await api.get("/admin/audit-logs", { params: buildParams(pg, f) });
      setData((prev) => append ? { total: r.data.total, results: [...prev.results, ...r.data.results] } : r.data);
      setPage(pg);
    } finally { append ? setLoadingMore(false) : setLoading(false); }
  };

  useEffect(() => { fetchPage(1, false); /* eslint-disable-next-line */ }, []);

  const apply = () => fetchPage(1, false, filters);
  const clear = () => { setFilters(EMPTY_AUDIT_FILTERS); fetchPage(1, false, EMPTY_AUDIT_FILTERS); };
  const set = (k, v) => setFilters({ ...filters, [k]: v });

  const { user: me } = useAuth();
  const exportCsv = async () => {
    const tid = toast.loading("Generando auditoría…");
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v !== "") params[k] = v; });
      const res = await api.get("/admin/export/audit.csv", { params, responseType: "blob" });
      const date = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `reencuentro-auditoria-${date}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Auditoría exportada.", { id: tid });
    } catch (e) {
      toast.error(e.response?.status === 403 ? "No autorizado para exportar auditoría." : "No se pudo exportar la auditoría.", { id: tid });
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-audit">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 bg-white border border-[#E2E8F0] rounded-xl p-3">
        <Select value={filters.action} onChange={(v) => set("action", v)} testid="audit-filter-action">
          <option value="">Acción (todas)</option>
          {AUDIT_ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Select>
        <Select value={filters.actor_role} onChange={(v) => set("actor_role", v)} testid="audit-filter-role">
          <option value="">Rol (todos)</option>
          {AUDIT_ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Select>
        <Select value={filters.entity_type} onChange={(v) => set("entity_type", v)} testid="audit-filter-entity">
          <option value="">Entidad (todas)</option>
          {AUDIT_ENTITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Select>
        <input value={filters.actor} onChange={(e) => set("actor", e.target.value)} data-testid="audit-filter-actor"
          placeholder="Actor (email o nombre)" className="h-10 px-3 rounded-lg border border-[#E2E8F0] text-sm" />
        <input value={filters.case_code} onChange={(e) => set("case_code", e.target.value)} data-testid="audit-filter-case"
          placeholder="Código de caso (RE-XXXXXX)" className="h-10 px-3 rounded-lg border border-[#E2E8F0] text-sm" />
        <div className="flex gap-2">
          <label className="flex-1 text-xs text-[#94A3B8]">Desde
            <input type="date" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} data-testid="audit-filter-from"
              className="w-full h-10 px-2 rounded-lg border border-[#E2E8F0] text-sm" /></label>
          <label className="flex-1 text-xs text-[#94A3B8]">Hasta
            <input type="date" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} data-testid="audit-filter-to"
              className="w-full h-10 px-2 rounded-lg border border-[#E2E8F0] text-sm" /></label>
        </div>
        <div className="flex gap-2 sm:col-span-2 lg:col-span-3">
          <button onClick={apply} data-testid="audit-filter-apply"
            className="h-10 px-5 rounded-lg bg-[#1E3A8A] text-white text-sm font-semibold">Filtrar</button>
          <button onClick={clear} data-testid="audit-filter-clear"
            className="h-10 px-4 rounded-lg bg-[#F1F5F9] text-[#475569] text-sm font-medium">Limpiar filtros</button>
          {me?.role === "super_admin" && (
            <button onClick={exportCsv} data-testid="audit-export-btn"
              className="h-10 px-4 rounded-lg bg-[#F1F5F9] text-[#0F172A] text-sm font-medium flex items-center gap-1.5 ml-auto">
              <Download className="w-4 h-4" /> Exportar auditoría CSV
            </button>
          )}
        </div>
      </div>

      <p className="text-sm text-[#475569]" data-testid="audit-count">Mostrando {data.results.length} de {data.total} registros</p>

      {loading ? (
        <p className="text-[#475569]">Cargando…</p>
      ) : data.results.length === 0 ? (
        <p className="text-sm text-[#94A3B8] py-8 text-center" data-testid="audit-empty">No hay registros de auditoría que coincidan con estos filtros.</p>
      ) : (
        <div className="space-y-2">
          {data.results.map((l) => (
            <div key={l.id} className="bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 flex items-center justify-between gap-3" data-testid={`audit-row-${l.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-[#0F172A]">{l.action_label}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-[#EFF6FF] text-[#1E40AF]">{l.entity_type}</span>
                  {l.public_case_code && <span className="text-xs font-mono text-[#94A3B8]">{l.public_case_code}</span>}
                </div>
                <p className="text-xs text-[#64748B] truncate">{l.actor_email || l.actor_role} · {l.actor_role} · {l.created_at ? new Date(l.created_at).toLocaleString() : ""}</p>
              </div>
              <button onClick={() => setDetail(l)} data-testid={`audit-detail-${l.id}`}
                className="text-xs text-[#1E3A8A] font-medium underline shrink-0">Ver detalle</button>
            </div>
          ))}
        </div>
      )}

      {!loading && data.results.length < data.total && (
        <div className="flex justify-center pt-1">
          <button onClick={() => fetchPage(page + 1, true)} disabled={loadingMore} data-testid="audit-load-more"
            className="h-11 px-8 rounded-xl bg-white border border-[#1E3A8A] text-[#1E3A8A] font-semibold disabled:opacity-50">
            {loadingMore ? "Cargando…" : `Cargar más (${data.total - data.results.length} restantes)`}
          </button>
        </div>
      )}

      {detail && <AuditDetail log={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function AuditDetail({ log, onClose }) {
  const fmt = (v) => v == null ? "—" : (typeof v === "object" ? JSON.stringify(v, null, 2) : String(v));
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="audit-detail-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full overflow-y-auto p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Detalle de auditoría</h2>
          <button onClick={onClose} data-testid="audit-detail-close"><X className="w-5 h-5" /></button>
        </div>
        <KV k="Acción" v={`${log.action_label} (${log.action})`} />
        <KV k="Actor" v={log.actor_email || "—"} />
        <KV k="Rol" v={log.actor_role} />
        <KV k="Fecha/hora" v={log.created_at ? new Date(log.created_at).toLocaleString() : "—"} />
        <KV k="Entidad" v={log.entity_type} />
        <KV k="ID de entidad" v={log.entity_id} />
        <KV k="Código de caso" v={log.public_case_code} />
        <KV k="IP" v={log.ip_address} />
        <KV k="User agent" v={log.user_agent} />
        <div className="border-t border-[#E2E8F0] pt-2">
          <p className="text-xs font-semibold text-[#475569] mb-1">Valores anteriores</p>
          <pre className="text-xs bg-[#F8FAFC] rounded-lg p-2 overflow-x-auto whitespace-pre-wrap" data-testid="audit-old-value">{fmt(log.old_value)}</pre>
        </div>
        <div>
          <p className="text-xs font-semibold text-[#475569] mb-1">Valores nuevos</p>
          <pre className="text-xs bg-[#F8FAFC] rounded-lg p-2 overflow-x-auto whitespace-pre-wrap" data-testid="audit-new-value">{fmt(log.new_value)}</pre>
        </div>
        <p className="text-xs text-[#94A3B8]">Los campos sensibles se enmascaran (***) para preservar la privacidad.</p>
      </div>
    </div>
  );
}

function DeletedTab({ refreshCounts }) {
  const { user: me } = useAuth();
  const isSuper = me?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [permTarget, setPermTarget] = useState(null);
  const [permStep, setPermStep] = useState(1);
  const [permText, setPermText] = useState("");
  const [permReason, setPermReason] = useState("");

  const load = () => api.get("/admin/deleted-cases").then((r) => setItems(r.data.results));
  useEffect(() => { load(); }, []);
  const after = () => { load(); refreshCounts?.(); };

  const doRestore = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/cases/${restoreTarget.id}/restore`);
      toast.success("Caso restaurado.");
      setRestoreTarget(null);
      after();
    } catch (e) { toast.error("No se pudo restaurar el caso."); }
    finally { setBusy(false); }
  };

  const closePerm = () => { setPermTarget(null); setPermStep(1); setPermText(""); setPermReason(""); };
  const doPermanent = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/cases/${permTarget.id}/permanent`, { data: { confirm: permText, reason: permReason || null } });
      toast.success("Caso eliminado definitivamente. Archivos retenidos de forma restringida.");
      closePerm();
      after();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar definitivamente.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="admin-deleted">
      <p className="text-sm text-[#475569]">{items.length} caso(s) eliminado(s).</p>
      {items.length === 0 && <p className="text-sm text-[#94A3B8] py-8 text-center">No hay casos eliminados.</p>}
      {items.map((c) => (
        <div key={c.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-2" data-testid={`deleted-case-${c.public_case_code}`}>
          <div className="flex items-center gap-1.5 flex-wrap">
            <TypeBadge caseType={c.case_type} label={c.case_type_label} />
            <RiskBadge level={c.risk_level} />
            {c.is_minor_case && <MinorBadge />}
            <span className="text-xs font-medium px-2 py-0.5 rounded bg-[#EFF6FF] text-[#1E40AF]">Estado: {c.status_label || c.status}</span>
            <span className="text-xs font-mono text-[#94A3B8]">{c.public_case_code}</span>
          </div>
          <p className="font-medium text-sm">{c.first_name || c.nickname || "Sin nombre"}</p>
          <div className="text-xs text-[#64748B] space-y-0.5">
            <p>Eliminado: {c.deleted_at ? new Date(c.deleted_at).toLocaleString() : "—"}</p>
            <p>Por: {c.deleted_by_email || "—"}</p>
            {c.deletion_reason && <p>Motivo: {c.deletion_reason}</p>}
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={() => setRestoreTarget(c)} disabled={busy} data-testid={`restore-${c.public_case_code}`}
              className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#D1FAE5] text-[#065F46] text-sm font-medium disabled:opacity-50">
              <RotateCcw className="w-4 h-4" /> Restaurar caso
            </button>
            {isSuper ? (
              <button onClick={() => { setPermTarget(c); setPermStep(1); setPermText(""); }} disabled={busy} data-testid={`permanent-${c.public_case_code}`}
                className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#991B1B] text-white text-sm font-medium disabled:opacity-50">
                <Trash2 className="w-4 h-4" /> Eliminar definitivamente
              </button>
            ) : (
              <button disabled title="Solo Super Admin" data-testid={`permanent-disabled-${c.public_case_code}`}
                className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#F1F5F9] text-[#94A3B8] text-sm font-medium cursor-not-allowed">
                <Trash2 className="w-4 h-4" /> Eliminar definitivamente
              </button>
            )}
          </div>
        </div>
      ))}

      {restoreTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="restore-dialog">
          <div className="absolute inset-0 bg-black/40" onClick={() => setRestoreTarget(null)} />
          <div className="relative bg-white rounded-2xl p-5 max-w-sm w-full space-y-3">
            <h3 className="font-heading text-lg font-bold text-[#0F172A]">¿Restaurar caso?</h3>
            <p className="text-sm text-[#475569]">El caso volverá a estar disponible en las listas internas y podrá volver a mostrarse públicamente si su estado y visibilidad lo permiten.</p>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setRestoreTarget(null)} disabled={busy} data-testid="restore-cancel"
                className="flex-1 h-12 rounded-xl bg-[#F1F5F9] text-[#0F172A] font-semibold disabled:opacity-50">Cancelar</button>
              <button onClick={doRestore} disabled={busy} data-testid="restore-confirm"
                className="flex-1 h-12 rounded-xl bg-[#1E3A8A] text-white font-semibold disabled:opacity-50">Restaurar caso</button>
            </div>
          </div>
        </div>
      )}

      {permTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="permanent-dialog">
          <div className="absolute inset-0 bg-black/50" onClick={closePerm} />
          <div className="relative bg-white rounded-2xl p-5 max-w-sm w-full space-y-3">
            {permStep === 1 ? (
              <>
                <h3 className="font-heading text-lg font-bold text-[#991B1B]">¿Eliminar definitivamente?</h3>
                <p className="text-sm text-[#475569]">Esta acción eliminará permanentemente el caso del sistema. No podrá restaurarse desde la aplicación.</p>
                <div className="flex gap-3 pt-1">
                  <button onClick={closePerm} disabled={busy} data-testid="permanent-cancel"
                    className="flex-1 h-12 rounded-xl bg-[#F1F5F9] text-[#0F172A] font-semibold disabled:opacity-50">Cancelar</button>
                  <button onClick={() => setPermStep(2)} disabled={busy} data-testid="permanent-continue"
                    className="flex-1 h-12 rounded-xl bg-[#991B1B] text-white font-semibold disabled:opacity-50">Continuar</button>
                </div>
              </>
            ) : (
              <>
                <h3 className="font-heading text-lg font-bold text-[#991B1B]">Confirmación final</h3>
                <p className="text-sm text-[#475569]">Escribe <b>ELIMINAR</b> para confirmar la eliminación definitiva.</p>
                <input value={permText} onChange={(e) => setPermText(e.target.value)} data-testid="permanent-text"
                  className="w-full h-12 px-3 rounded-lg border border-[#E2E8F0]" placeholder="ELIMINAR" />
                <input value={permReason} onChange={(e) => setPermReason(e.target.value)} data-testid="permanent-reason"
                  className="w-full h-11 px-3 rounded-lg border border-[#E2E8F0] text-sm" placeholder="Motivo (opcional)" />
                <p className="text-xs text-[#94A3B8]">Los archivos asociados quedan retenidos de forma restringida hasta política final de retención.</p>
                <div className="flex gap-3 pt-1">
                  <button onClick={closePerm} disabled={busy} data-testid="permanent-cancel-2"
                    className="flex-1 h-12 rounded-xl bg-[#F1F5F9] text-[#0F172A] font-semibold disabled:opacity-50">Cancelar</button>
                  <button onClick={doPermanent} disabled={busy || permText !== "ELIMINAR"} data-testid="permanent-confirm"
                    className="flex-1 h-12 rounded-xl bg-[#991B1B] text-white font-semibold disabled:opacity-50">
                    {busy ? "Eliminando…" : "Eliminar definitivamente"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Select({ value, onChange, children, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="h-10 px-3 rounded-lg border border-[#E2E8F0] bg-white text-sm">{children}</select>
  );
}
function Section({ title, children }) {
  return (
    <div className="border-t border-[#E2E8F0] pt-3">
      <h3 className="font-heading font-semibold text-sm mb-2">{title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function KV({ k, v }) {
  return <div className="flex justify-between gap-3 text-sm"><span className="text-[#94A3B8]">{k}</span><span className="text-[#0F172A] text-right break-words">{v || "—"}</span></div>;
}
